import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Image, Select, Space, Spin, Tooltip, message as toast } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, RollbackOutlined, UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { ClassPill, PersonAvatar, PriorityPill, StatusPill } from 'pages/support-shared/components';
import {
  AgentOption,
  MessagesPage,
  SupportMessageRow,
  SupportMeta,
  SupportTemplate,
  ThreadDetail,
} from 'pages/support-shared/types';
import { chatTime, durationText, SOURCE_LABEL, supportError, usePolling } from 'pages/support-shared/utils';
import Composer, { SendPayload } from './composer';
import styles from './styles.module.scss';

interface Props {
  threadId: number;
  meId?: number;
  meta: SupportMeta | null;
  agents: AgentOption[];
  templates: SupportTemplate[];
  signAgentName: boolean;
  supportName: string;
  detail: ThreadDetail | null;
  onDetail: (detail: ThreadDetail) => void;
  /** Luồng đổi trạng thái/người nhận → danh sách + số đếm cập nhật. */
  onChanged: () => void;
  /** Màn hẹp (một cột): quay lại danh sách. */
  onBack?: () => void;
}

const PAGE = 30;
const fileUrl = (path: string) => `${SERVER_FILE_ENDPOINT}/${path.replace(/^\/+/, '')}`;

const Conversation = ({
  threadId,
  meId,
  meta,
  agents,
  templates,
  signAgentName,
  supportName,
  detail,
  onDetail,
  onChanged,
  onBack,
}: Props) => {
  const [messages, setMessages] = useState<Map<number, SupportMessageRow>>(new Map());
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);
  const [readByUserAt, setReadByUserAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastSeenId = useRef(0);
  const olderAnchor = useRef<number | null>(null);
  // Hàm của trang cha đổi mỗi lần render → giữ trong ref để các callback dưới
  // chỉ phụ thuộc threadId (không thì effect đổi luồng chạy lại liên tục).
  const onDetailRef = useRef(onDetail);
  const onChangedRef = useRef(onChanged);
  onDetailRef.current = onDetail;
  onChangedRef.current = onChanged;

  const merge = (rows: SupportMessageRow[]) =>
    setMessages((prev) => {
      const next = new Map(prev);
      rows.forEach((row) => next.set(row.id, row));
      return next;
    });

  const markRead = useCallback(() => {
    AxiosUtils.post(`${API.supportThreads}/${threadId}/read`, {})
      .then(() => onChangedRef.current())
      .catch(() => undefined);
  }, [threadId]);

  const loadDetail = useCallback(() => {
    AxiosUtils.get(`${API.supportThreads}/${threadId}`)
      .then((res: any) => onDetailRef.current(res.data))
      .catch(() => undefined);
  }, [threadId]);

  const loadLatest = useCallback(
    async (initial = false) => {
      try {
        const res: any = await AxiosUtils.get(`${API.supportThreads}/${threadId}/messages`, { limit: PAGE });
        const page: MessagesPage = res.data;
        merge(page.data);
        setReadByUserAt(page.readByUserAt);
        if (initial) {
          setHasMore(page.metadata.hasMore);
          setCursor(page.metadata.nextCursor);
        }
        const newest = page.data[0];
        if (newest && newest.id > lastSeenId.current) {
          const hadSeen = lastSeenId.current;
          lastSeenId.current = newest.id;
          // Có tin mới của người dùng khi đang mở luồng → coi như đã xem.
          if (!initial && hadSeen && page.data.some((m) => m.id > hadSeen && m.direction === 'IN')) markRead();
        }
      } catch (error) {
        if (initial) toast.error(supportError(error, 'Không tải được tin nhắn.'));
      } finally {
        if (initial) setLoading(false);
      }
    },
    [threadId, markRead]
  );

  // Đổi luồng → nạp lại từ đầu.
  useEffect(() => {
    setMessages(new Map());
    setLoading(true);
    setHasMore(false);
    setCursor(null);
    lastSeenId.current = 0;
    stickToBottom.current = true;
    loadLatest(true);
    loadDetail();
    markRead();
  }, [threadId, loadLatest, loadDetail, markRead]);

  usePolling(() => loadLatest(false), 4000);
  usePolling(loadDetail, 15000);

  const loadOlder = async () => {
    if (!cursor) return;
    setLoadingOlder(true);
    olderAnchor.current = scrollRef.current ? scrollRef.current.scrollHeight - scrollRef.current.scrollTop : null;
    try {
      const res: any = await AxiosUtils.get(`${API.supportThreads}/${threadId}/messages`, {
        limit: PAGE,
        before: cursor,
      });
      const page: MessagesPage = res.data;
      merge(page.data);
      setHasMore(page.metadata.hasMore);
      setCursor(page.metadata.nextCursor);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setLoadingOlder(false);
    }
  };

  const ordered = Array.from(messages.values()).sort((a, b) => a.id - b.id);
  const threadReady = !!detail && detail.thread.id === threadId;
  const keepBottom = () => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  };

  // Giữ vị trí cuộn: xem tin cũ thì không nhảy; có tin mới khi đang ở cuối thì kéo xuống.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (olderAnchor.current !== null) {
      el.scrollTop = el.scrollHeight - olderAnchor.current;
      olderAnchor.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
    // `threadReady`: khung tin chỉ xuất hiện khi chi tiết luồng đã tải — lúc đó mới cuộn được.
  }, [ordered.length, loading, threadReady]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const patch = async (body: Record<string, any>, okText?: string) => {
    setBusy(true);
    try {
      const res: any = await AxiosUtils.patch(`${API.supportThreads}/${threadId}`, body);
      onDetail(res.data);
      onChanged();
      if (okText) toast.success(okText);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setBusy(false);
    }
  };

  const claim = async (force = false) => {
    setBusy(true);
    try {
      const res: any = await AxiosUtils.post(`${API.supportThreads}/${threadId}/claim`, { force });
      onDetail(res.data);
      onChanged();
      toast.success('Đã nhận xử lý.');
    } catch (error: any) {
      if (error?.statusCode === 409 && !force) {
        toast.warning(supportError(error));
      } else {
        toast.error(supportError(error));
      }
    } finally {
      setBusy(false);
    }
  };

  const send = async ({ content, files, templateId, resolve }: SendPayload): Promise<boolean> => {
    try {
      const url = `${API.supportThreads}/${threadId}/reply`;
      if (files.length) {
        const form = new FormData();
        if (content) form.append('content', content);
        if (templateId) form.append('templateId', String(templateId));
        if (resolve) form.append('resolve', 'true');
        files.forEach((file) => form.append('attachments', file));
        await AxiosUtils.post(url, form, { 'Content-Type': 'multipart/form-data' });
      } else {
        await AxiosUtils.post(url, { content, templateId, resolve });
      }
      stickToBottom.current = true;
      await loadLatest(false);
      loadDetail();
      onChanged();
      if (resolve) toast.success('Đã gửi và đánh dấu Đã xong.');
      return true;
    } catch (error) {
      toast.error(supportError(error, 'Gửi không thành công.'));
      return false;
    }
  };

  const thread = detail?.thread;
  if (!thread || thread.id !== threadId) {
    return (
      <div className={`${styles.column} ${styles.chat}`}>
        <div className={styles.chatEmpty}>
          <Spin />
        </div>
      </div>
    );
  }

  const holder = thread.assignee && thread.assignee.id !== meId ? thread.assignee : null;
  const lastOut = [...ordered].reverse().find((m) => m.direction === 'OUT' && !m.recalled);
  const seen = !!(lastOut && readByUserAt && new Date(readByUserAt) >= new Date(lastOut.createdAt));

  let lastDay = '';
  return (
    <div className={`${styles.column} ${styles.chat}`}>
      <div className={styles.chatHead}>
        <div className={styles.chatTitleRow}>
          {onBack && (
            <Button
              className={styles.backBtn}
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              aria-label="Quay lại danh sách"
            />
          )}
          <PersonAvatar name={thread.user.name} avatar={thread.user.avatar} size={40} />
          <div className={styles.chatTitle}>
            <h2>{thread.user.name}</h2>
            <div className={styles.chatSub}>
              <ClassPill value={thread.userClass} label={thread.userClassLabel} />
              <StatusPill status={thread.status} />
              <PriorityPill priority={thread.priority} />
              {thread.status === 'OPEN' && thread.awaitingSince && <span>chờ {durationText(thread.awaitingSince)}</span>}
              {thread.user.username && <span>@{thread.user.username}</span>}
            </div>
          </div>
          <Space wrap>
            {thread.status !== 'RESOLVED' ? (
              <Button icon={<CheckOutlined />} onClick={() => patch({ status: 'RESOLVED' }, 'Đã đánh dấu Đã xong.')} loading={busy}>
                Đã xong
              </Button>
            ) : (
              <Button icon={<RollbackOutlined />} onClick={() => patch({ status: 'OPEN' }, 'Đã mở lại.')} loading={busy}>
                Mở lại
              </Button>
            )}
          </Space>
        </div>
        <div className={styles.chatControls}>
          {thread.assignee?.id !== meId && (
            <Button size="small" type="primary" ghost icon={<UserAddOutlined />} onClick={() => claim(false)} loading={busy}>
              Nhận xử lý
            </Button>
          )}
          <Select
            size="small"
            style={{ minWidth: 190 }}
            placeholder="Người phụ trách"
            // 0 = "Chưa giao" (antd coi value null là chưa chọn → chỉ hiện placeholder).
            value={thread.assignee?.id ?? 0}
            onChange={(value: number) => patch({ assigneeId: value || null }, value ? 'Đã giao việc.' : 'Đã bỏ giao.')}
            options={[
              { value: 0, label: 'Chưa giao' },
              ...agents.map((a: AgentOption) => ({ value: a.id, label: a.id === meId ? `${a.name} (tôi)` : a.name })),
            ]}
            disabled={busy}
            popupMatchSelectWidth={false}
          />
          <Select
            size="small"
            style={{ minWidth: 170 }}
            placeholder="Loại vấn đề"
            allowClear
            value={thread.category ?? undefined}
            onChange={(value) => patch({ category: value ?? null })}
            options={meta?.categories || []}
            disabled={busy}
            popupMatchSelectWidth={false}
          />
          <Select
            size="small"
            style={{ minWidth: 120 }}
            value={thread.priority}
            onChange={(value) => patch({ priority: value })}
            options={meta?.priorities || []}
            disabled={busy}
          />
        </div>
      </div>

      <div className={styles.messages} ref={scrollRef} onScroll={onScroll}>
        {loading ? (
          <div className={styles.chatEmpty}>
            <Spin />
          </div>
        ) : (
          <>
            {hasMore && (
              <Button className={styles.loadOlder} size="small" onClick={loadOlder} loading={loadingOlder}>
                Xem tin cũ hơn
              </Button>
            )}
            {!ordered.length && <div className={styles.chatEmpty}>Chưa có tin nhắn.</div>}
            {ordered.map((m) => {
              const day = dayjs(m.createdAt).format('DD/MM/YYYY');
              const divider = day !== lastDay ? (lastDay = day) : null;
              const out = m.direction === 'OUT';
              const auto = out && (m.source === 'AUTO' || m.source === 'WELCOME');
              const bubbleClass = m.recalled
                ? styles.bubbleRecalled
                : auto
                ? styles.bubbleAuto
                : out
                ? styles.bubbleOut
                : styles.bubbleIn;
              const sourceLabel = out ? SOURCE_LABEL[m.source] : '';
              return (
                <div key={m.id} style={{ display: 'contents' }}>
                  {divider && (
                    <div className={styles.dayDivider}>
                      {dayjs(m.createdAt).isSame(dayjs(), 'day') ? 'Hôm nay' : divider}
                    </div>
                  )}
                  <div className={`${styles.row} ${out ? styles.rowOut : styles.rowIn}`}>
                    {!!m.attachments.length && (
                      <div className={styles.images}>
                        <Image.PreviewGroup>
                          {m.attachments.map((a) => (
                            <Image
                              key={a.key}
                              width={140}
                              height={140}
                              style={{ objectFit: 'cover' }}
                              src={fileUrl(a.preview)}
                              preview={{ src: fileUrl(a.original) }}
                              alt="Ảnh đính kèm"
                              onLoad={keepBottom}
                            />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}
                    {(m.content || m.recalled) && (
                      <div className={`${styles.bubble} ${bubbleClass}`}>{m.content || '(chỉ có ảnh)'}</div>
                    )}
                    {m.edited && m.originalContent && !m.recalled && (
                      <div className={styles.original}>Bản gốc: “{m.originalContent}”</div>
                    )}
                    <div className={styles.rowMeta}>
                      <span>{chatTime(m.createdAt)}</span>
                      {out && m.agent && <span>· {m.agent.name}</span>}
                      {sourceLabel && (
                        <span>
                          · {sourceLabel}
                          {m.template?.title ? `: ${m.template.title}` : ''}
                        </span>
                      )}
                      {m.edited && <span>· đã sửa</span>}
                      {m.recalled && (
                        <Tooltip title="Người dùng đã xoá tin này trong app; bản lưu chỉ nhân viên thấy.">
                          <span>· người dùng đã thu hồi</span>
                        </Tooltip>
                      )}
                      {out && lastOut?.id === m.id && <span>· {seen ? 'đã xem' : 'đã gửi'}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <Composer
        templates={templates}
        variables={detail?.templateVariables || {}}
        holderName={holder?.name}
        onClaim={holder ? () => claim(true) : undefined}
        footerNote={
          signAgentName
            ? `Người dùng thấy tin từ “${supportName}”, cuối tin có tên bạn.`
            : `Người dùng thấy tin từ “${supportName}”, không thấy tên bạn.`
        }
        onSend={send}
      />
    </div>
  );
};

export default Conversation;
